import React, { useEffect, useMemo, useCallback, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Search,
  RefreshCw,
  Download,
  Building2,
  ChevronRight,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Users,
  UserCheck,
  UserX,
  TrendingUp,
  MapPin
} from 'lucide-react';
import Header from '../../../../components/header/Header';
import Footer from '../../../../components/footer/Footer';
import HeroCarousel from '../../../../components/carousel/HeroCarousel';
import Breadcrumb from '../../../../shared/components/Breadcrumb';
import img1 from '../../../../assets/attendenceCarousel.png';
import {
  DateRangePicker,
  Pagination
} from '../../components/analytics';
import { attendanceApi } from '../../services/api';
import type { ROAttendanceSummary } from '../../types/analytics';
import { useAttendanceCache } from '../../context/AttendanceContext';

const PAGE_SIZE = 25;

// Sort field type for RO table
type ROSortField = 'roName' | 'totalSchools' | 'totalStaff' | 'presentPercentageReg' | 'presentPercentageCon' | 'overallAttendancePercent';

interface ROTableSortConfig {
  field: ROSortField;
  order: 'asc' | 'desc';
}

// Transform API response to ROAttendanceSummary format
const transformAPIDataToROList = (apiData: any[]): ROAttendanceSummary[] => {
  return apiData.map((item: any) => {
    const totalEnrollmentReg = parseInt(item.totalEnrollmentReg) || 0;
    const totalEnrollmentCon = parseInt(item.totalEnrollmentCon) || 0;
    const totalStaff = totalEnrollmentReg + totalEnrollmentCon;
    const presentPercentageReg = parseFloat(item.presentPercentageReg) || 0;
    const presentPercentageCon = parseFloat(item.presentPercentageCon) || 0;
    
    // Calculate overall attendance percentage (weighted average)
    const overallAttendancePercent = totalStaff > 0 
      ? Math.round(((presentPercentageReg * totalEnrollmentReg + presentPercentageCon * totalEnrollmentCon) / totalStaff) * 10) / 10
      : 0;

    return {
      roId: item.roID,
      roName: item.roName || '',
      totalSchools: parseInt(item.totalSchools) || 0,
      totalEnrollmentReg,
      totalEnrollmentCon,
      totalStaff,
      presentPercentageReg: Math.round(presentPercentageReg * 10) / 10,
      absentPercentageReg: Math.round((parseFloat(item.absentPercentageReg) || 0) * 10) / 10,
      presentPercentageCon: Math.round(presentPercentageCon * 10) / 10,
      absentPercentageCon: Math.round((parseFloat(item.absentPercentageCon) || 0) * 10) / 10,
      overallAttendancePercent
    };
  });
};

// Generate overall summary from the RO list
const generateROSummary = (list: ROAttendanceSummary[]) => {
  const totalROs = list.length;
  const totalSchools = list.reduce((s, ro) => s + (ro.totalSchools || 0), 0);
  const totalRegularStaff = list.reduce((s, ro) => s + (ro.totalEnrollmentReg || 0), 0);
  const totalContractualStaff = list.reduce((s, ro) => s + (ro.totalEnrollmentCon || 0), 0);
  const totalStaff = totalRegularStaff + totalContractualStaff;

  // Weighted attendance calculation
  const weightedRegularPresent = list.reduce((s, ro) => 
    s + ((ro.totalEnrollmentReg || 0) * (ro.presentPercentageReg || 0) / 100), 0);
  const weightedContractualPresent = list.reduce((s, ro) => 
    s + ((ro.totalEnrollmentCon || 0) * (ro.presentPercentageCon || 0) / 100), 0);
  const totalPresent = Math.round(weightedRegularPresent + weightedContractualPresent);
  const totalAbsent = totalStaff - totalPresent;

  const overallAttendancePercent = totalStaff > 0 ? Math.round((totalPresent / totalStaff) * 100 * 10) / 10 : 0;
  const regularAttendancePercent = totalRegularStaff > 0 ? Math.round((weightedRegularPresent / totalRegularStaff) * 100 * 10) / 10 : 0;
  const contractualAttendancePercent = totalContractualStaff > 0 ? Math.round((weightedContractualPresent / totalContractualStaff) * 100 * 10) / 10 : 0;

  return {
    totalROs,
    totalSchools,
    totalStaff,
    totalRegularStaff,
    totalContractualStaff,
    totalPresent,
    totalAbsent,
    overallAttendancePercent,
    regularAttendancePercent,
    contractualAttendancePercent
  };
};

// Local pagination helper
const paginateList = <T,>(list: T[], page: number, pageSize: number): { data: T[]; totalPages: number; totalRecords: number } => {
  const validatedPage = Math.max(1, page || 1);
  const startIndex = (validatedPage - 1) * pageSize;
  return {
    data: list.slice(startIndex, startIndex + pageSize),
    totalPages: Math.max(1, Math.ceil(list.length / pageSize)),
    totalRecords: list.length
  };
};

const ROAttendanceList: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const carouselImages = [img1];
  
  const { countsData, setCachedCounts } = useAttendanceCache();

  useEffect(() => {
    if (!countsData.totalEmployee) {
      attendanceApi.getTotalEmployees().then((data) => {
        if (data !== null && typeof data.totalEmployee === 'number' && typeof data.totalRegisteredEmp === 'number' && typeof data.totalContractEmpCount === 'number') {
          setCachedCounts('totalEmployee', data);
        }
      });
    }
  }, [countsData.totalEmployee, setCachedCounts]);

  // API data state
  const [roList, setRoList] = useState<ROAttendanceSummary[]>([]);
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(() => {
    const page = searchParams.get('page');
    return page ? Number(page) : 1;
  });
  
  // Date range state (default to last 30 days)
  const [dateRange, setDateRange] = useState<{ fromDate: string; toDate: string }>(() => {
    const fromParam = searchParams.get('fromDate');
    const toParam = searchParams.get('toDate');
    
    if (fromParam && toParam) {
      return { fromDate: fromParam, toDate: toParam };
    }
    
    const today = new Date();
    const thirtyDaysAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
    return {
      fromDate: thirtyDaysAgo.toISOString().split('T')[0],
      toDate: today.toISOString().split('T')[0]
    };
  });

  const [sortConfig, setSortConfig] = useState<ROTableSortConfig>({
    field: 'overallAttendancePercent',
    order: 'desc'
  });
  const [isLoading, setIsLoading] = useState(true);

  // Fetch data from API
  const fetchData = useCallback(async () => {
    setIsLoading(true);

    try {
      const response = await attendanceApi.getROAttendanceSummary(dateRange.fromDate, dateRange.toDate);

      if (response && response.data) {
        const transformedData = transformAPIDataToROList(response.data);
        setRoList(transformedData);
      } else {
        setRoList([]);
      }
    } catch (err) {
      console.error('Error fetching RO attendance data:', err);
      setRoList([]);
    } finally {
      setIsLoading(false);
    }
  }, [dateRange]);

  // Load data on mount and when date range changes
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (dateRange.fromDate) params.set('fromDate', dateRange.fromDate);
    if (dateRange.toDate) params.set('toDate', dateRange.toDate);
    if (currentPage > 1) params.set('page', String(currentPage));

    setSearchParams(params, { replace: true });
  }, [dateRange, currentPage, setSearchParams]);

  // Filtered and sorted data
  const { filteredList, paginatedData, summary, totalRecords, totalPages } = useMemo(() => {
    // Search filter
    let filtered = [...roList];
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      filtered = filtered.filter(ro =>
        (ro.roName || '').toLowerCase().includes(q) ||
        String(ro.roId || '').includes(q)
      );
    }

    // Sort
    const sorted = [...filtered].sort((a, b) => {
      const aValue = a[sortConfig.field] ?? '';
      const bValue = b[sortConfig.field] ?? '';

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.order === 'asc' ? aValue - bValue : bValue - aValue;
      }

      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();
      return sortConfig.order === 'asc' ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
    });

    const paginated = paginateList(sorted, currentPage, PAGE_SIZE);
    const summaryData = generateROSummary(filtered);

    return {
      filteredList: sorted,
      paginatedData: paginated,
      summary: summaryData,
      totalRecords: paginated.totalRecords,
      totalPages: paginated.totalPages
    };
  }, [roList, searchQuery, sortConfig, currentPage]);

  // Handle date range change
  const handleDateRangeChange = useCallback((fromDate: string, toDate: string) => {
    setDateRange({ fromDate, toDate });
    setCurrentPage(1);
  }, []);

  // Handle page change
  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Handle sort
  const handleSort = useCallback((field: ROSortField) => {
    setSortConfig(prev => ({
      field,
      order: prev.field === field && prev.order === 'desc' ? 'asc' : 'desc'
    }));
  }, []);

  // Handle refresh
  const handleRefresh = useCallback(() => {
    fetchData();
  }, [fetchData]);

  // Handle export
  const handleExport = useCallback(() => {
    if (!filteredList.length) return;

    const headers = ['#', 'Region Name', 'Total Schools', 'Regular %', 'Contractual %', 'Overall %'];
    const rows = filteredList.map((ro, idx) => [
      idx + 1,
      ro.roName,
      ro.totalSchools,
      ro.presentPercentageReg,
      ro.presentPercentageCon,
      ro.overallAttendancePercent
    ]);

    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ro-attendance-${dateRange.fromDate}-to-${dateRange.toDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [filteredList, dateRange]);

  // Handle RO row click - navigate to KVAttendanceList with roId and roName
  const handleROClick = useCallback((ro: ROAttendanceSummary) => {
    const params = new URLSearchParams();
    params.append('fromDate', dateRange.fromDate);
    params.append('toDate', dateRange.toDate);
    params.append('roId', String(ro.roId));
    params.append('roName', ro.roName);

    navigate(`/attendance/analytics/kv-list?${params.toString()}`);
  }, [dateRange, navigate]);

  // Get sort icon
  const getSortIcon = (field: ROSortField) => {
    if (sortConfig.field !== field) {
      return <ArrowUpDown className="w-4 h-4 text-gray-400" />;
    }
    return sortConfig.order === 'asc' 
      ? <ArrowUp className="w-4 h-4 text-[#6A1786]" />
      : <ArrowDown className="w-4 h-4 text-[#6A1786]" />;
  };

  // Get attendance color
  const getAttendanceColor = (percent: number) => {
    if (percent >= 90) return 'text-green-600 bg-green-50';
    if (percent >= 75) return 'text-yellow-600 bg-yellow-50';
    if (percent >= 50) return 'text-orange-600 bg-orange-50';
    return 'text-red-600 bg-red-50';
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#f5f5f7]">
      <Header />
      <HeroCarousel images={carouselImages} autoRotateInterval={3000} />

      <div className="w-full max-w-7xl mx-auto px-4">
        <Breadcrumb />
      </div>

      <main className="flex-grow w-full max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Page Header */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-[#6A1786]">Regional Office Attendance Analytics</h1>
            <p className="text-sm text-gray-500 mt-1">
              {summary.totalROs} Regions • {summary.totalSchools.toLocaleString()} KV Schools • {isLoading || !countsData.totalEmployee ? '--' : countsData.totalEmployee.totalEmployee.toLocaleString()} staff members
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleRefresh}
              disabled={isLoading}
              className="p-2 hover:bg-white hover:shadow rounded-lg transition-all disabled:opacity-50"
              title="Refresh data"
            >
              <RefreshCw className={`w-5 h-5 text-gray-600 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={handleExport}
              disabled={!filteredList.length}
              className="p-2 hover:bg-white hover:shadow rounded-lg transition-all disabled:opacity-50"
              title="Export to CSV"
            >
              <Download className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Total Staff Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-50 rounded-lg">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Staff</p>
                <p className="text-xl font-bold text-gray-900">
                  {isLoading || !countsData.totalEmployee ? '--' : countsData.totalEmployee.totalEmployee.toLocaleString()}
                </p>
                <div className="flex gap-2 text-xs mt-1">
                  <span className="text-blue-600">Reg: {isLoading || !countsData.totalEmployee ? '--' : countsData.totalEmployee.totalRegisteredEmp.toLocaleString()}</span>
                  <span className="text-purple-600">Con: {isLoading || !countsData.totalEmployee ? '--' : countsData.totalEmployee.totalContractEmpCount.toLocaleString()}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Overall Attendance Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-green-50 rounded-lg">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Overall Attendance</p>
                <p className="text-xl font-bold text-green-600">
                  {isLoading ? '--' : `${summary.overallAttendancePercent}%`}
                </p>
              </div>
            </div>
          </div>

          {/* Regular Attendance Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-50 rounded-lg">
                <UserCheck className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Regular Staff</p>
                <p className="text-xl font-bold text-blue-600">
                  {isLoading ? '--' : `${summary.regularAttendancePercent}%`}
                </p>
              </div>
            </div>
          </div>

          {/* Contractual Attendance Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-purple-50 rounded-lg">
                <UserCheck className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Contractual Staff</p>
                <p className="text-xl font-bold text-purple-600">
                  {isLoading ? '--' : `${summary.contractualAttendancePercent}%`}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters Bar */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
          <div className="flex flex-wrap items-center gap-4">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search regions..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#6A1786] focus:border-[#6A1786]"
              />
            </div>

            {/* Date Range Picker */}
            <DateRangePicker
              value={{ fromDate: dateRange.fromDate, toDate: dateRange.toDate }}
              onSubmit={(range) => {
                if (range.fromDate && range.toDate) {
                  handleDateRangeChange(range.fromDate, range.toDate);
                }
              }}
              disabled={isLoading}
            />
          </div>
        </div>

        {/* RO Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    #
                  </th>
                  <th 
                    className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('roName')}
                  >
                    <div className="flex items-center gap-1">
                      Region Name
                      {getSortIcon('roName')}
                    </div>
                  </th>
                  <th 
                    className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('totalSchools')}
                  >
                    <div className="flex items-center justify-center gap-1">
                      Schools
                      {getSortIcon('totalSchools')}
                    </div>
                  </th>
                  <th 
                    className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('presentPercentageReg')}
                  >
                    <div className="flex items-center justify-center gap-1">
                      Regular %
                      {getSortIcon('presentPercentageReg')}
                    </div>
                  </th>
                  <th 
                    className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('presentPercentageCon')}
                  >
                    <div className="flex items-center justify-center gap-1">
                      Contractual %
                      {getSortIcon('presentPercentageCon')}
                    </div>
                  </th>
                  <th 
                    className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('overallAttendancePercent')}
                  >
                    <div className="flex items-center justify-center gap-1">
                      Overall %
                      {getSortIcon('overallAttendancePercent')}
                    </div>
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {isLoading ? (
                  // Loading skeleton
                  Array.from({ length: 10 }).map((_, idx) => (
                    <tr key={idx} className="animate-pulse">
                      <td className="px-4 py-4"><div className="h-4 bg-gray-200 rounded w-8"></div></td>
                      <td className="px-4 py-4"><div className="h-4 bg-gray-200 rounded w-32"></div></td>
                      <td className="px-4 py-4"><div className="h-4 bg-gray-200 rounded w-12 mx-auto"></div></td>
                      <td className="px-4 py-4"><div className="h-4 bg-gray-200 rounded w-12 mx-auto"></div></td>
                      <td className="px-4 py-4"><div className="h-4 bg-gray-200 rounded w-12 mx-auto"></div></td>
                      <td className="px-4 py-4"><div className="h-4 bg-gray-200 rounded w-12 mx-auto"></div></td>
                      <td className="px-4 py-4"><div className="h-4 bg-gray-200 rounded w-16 mx-auto"></div></td>
                    </tr>
                  ))
                ) : paginatedData.data.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center text-gray-500">
                      <MapPin className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-lg font-medium">No regions found</p>
                      <p className="text-sm">Try adjusting your search or date range</p>
                    </td>
                  </tr>
                ) : (
                  paginatedData.data.map((ro, idx) => (
                    <tr 
                      key={ro.roId}
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => handleROClick(ro)}
                    >
                      <td className="px-4 py-4 text-sm text-gray-500">
                        {(currentPage - 1) * PAGE_SIZE + idx + 1}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          <div className="p-2 bg-purple-50 rounded-lg">
                            <Building2 className="w-4 h-4 text-[#6A1786]" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{ro.roName}</p>
                            <p className="text-xs text-gray-500">ID: {ro.roId}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-center">
                        <span className="font-medium text-gray-900">{ro.totalSchools}</span>
                      </td>
                      <td className="px-4 py-4 text-center">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getAttendanceColor(ro.presentPercentageReg)}`}>
                          {ro.presentPercentageReg}%
                        </span>
                      </td>
                      <td className="px-4 py-4 text-center">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getAttendanceColor(ro.presentPercentageCon)}`}>
                          {ro.presentPercentageCon}%
                        </span>
                      </td>
                      <td className="px-4 py-4 text-center">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-sm font-semibold ${getAttendanceColor(ro.overallAttendancePercent)}`}>
                          {ro.overallAttendancePercent}%
                        </span>
                      </td>
                      <td className="px-4 py-4 text-center">
                        <button 
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-[#6A1786] bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleROClick(ro);
                          }}
                        >
                          View KVs
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalRecords={totalRecords}
          pageSize={PAGE_SIZE}
          onPageChange={handlePageChange}
          isLoading={isLoading}
        />
      </main>

      <Footer />
    </div>
  );
};

export default ROAttendanceList;
